import { getValidToken } from '../utils/tokenManager';
import { environment } from '../config/environment';

export interface StoredQuestion {
  id: string;
  question: string;
  answers: string[];
  correct_index: number;
  explanation?: string;
  fact_check?: boolean;
}

export interface StoredQuiz {
  quiz_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  title: string;
  description: string;
  questions: StoredQuestion[];
  difficulty?: string;
  language?: string;
  is_public: boolean;
  play_count?: number;
}

export interface CreateQuizRequest {
  title: string;
  description: string;
  questions: StoredQuestion[];
  difficulty?: string;
  language?: string;
  is_public?: boolean;
}

export interface UpdateQuizRequest {
  title?: string;
  description?: string;
  questions?: StoredQuestion[];
  difficulty?: string;
  language?: string;
  is_public?: boolean;
}

export interface QuizListResponse {
  quizzes: StoredQuiz[];
  count: number;
}

const QUIZ_STORAGE_API_URL = environment.api.quizStorage;

const getAuthToken = async (): Promise<string | null> => {
  return await getValidToken();
};

export const createQuiz = async (quiz: CreateQuizRequest): Promise<StoredQuiz> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(quiz),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create quiz' }));
    throw new Error(error.error || 'Failed to create quiz');
  }

  return response.json();
};

export const listQuizzes = async (): Promise<QuizListResponse> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch quizzes' }));
    throw new Error(error.error || 'Failed to fetch quizzes');
  }

  return response.json();
};

export const getQuiz = async (quizId: string): Promise<StoredQuiz> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/${quizId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch quiz' }));
    throw new Error(error.error || 'Failed to fetch quiz');
  }

  return response.json();
};

export const updateQuiz = async (quizId: string, updates: UpdateQuizRequest): Promise<StoredQuiz> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/${quizId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update quiz' }));
    throw new Error(error.error || 'Failed to update quiz');
  }

  return response.json();
};

export const deleteQuiz = async (quizId: string): Promise<void> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/${quizId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete quiz' }));
    throw new Error(error.error || 'Failed to delete quiz');
  }
};

// Game Results API

export const saveGameResult = async (quizId: string, label: string, statistics: any): Promise<any> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const url = `${QUIZ_STORAGE_API_URL}/results`;
  console.log('🔧 saveGameResult API call:', {
    url,
    quizId,
    label,
    hasStatistics: !!statistics,
    statisticsKeys: statistics ? Object.keys(statistics) : [],
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        label,
        statistics,
      }),
    });

    console.log('🔧 saveGameResult response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: response.url,
    });

    if (!response.ok) {
      let errorDetails;
      try {
        errorDetails = await response.json();
      } catch {
        errorDetails = { error: `HTTP ${response.status}: ${response.statusText}` };
      }

      const errorMessage = errorDetails.error || `Failed to save game result (HTTP ${response.status})`;
      console.error('🔧 saveGameResult error details:', {
        status: response.status,
        statusText: response.statusText,
        errorDetails,
        quizId,
        label,
      });

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('🔧 saveGameResult success:', result);
    return result;
  } catch (error) {
    console.error('🔧 saveGameResult network/parse error:', {
      error,
      errorMessage: error instanceof Error ? error.message : String(error),
      quizId,
      label,
    });
    throw error;
  }
};

export const listGameResults = async (quizId: string): Promise<any> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/results?quiz_id=${quizId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch game results' }));
    throw new Error(error.error || 'Failed to fetch game results');
  }

  return response.json();
};

export const deleteGameResult = async (resultId: string): Promise<void> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${QUIZ_STORAGE_API_URL}/results/${resultId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete game result' }));
    throw new Error(error.error || 'Failed to delete game result');
  }
};